import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react'

// MUI
import {
  Box,
  IconButton,
  Typography,
  Button,
  Tooltip,
  CircularProgress,
} from '@mui/material'
import {
  Visibility,
  ZoomIn,
  ZoomOut,
  Print,
  Download,
  Refresh,
} from '@mui/icons-material'

// Components
import { CustomModal } from 'components/molecules/Modal/Modal'

// Hooks
import { useIsMobile } from 'lib/utils/useIsMobile'

// Types
import { ResponsivePdfPreviewModalProps } from './ResponsivePdfPreviewModal.types'

// Styles
import styles from './ResponsivePdfPreviewModal.module.scss'

type ZoomLevel = 50 | 75 | 100 | 125 | 150 | 200

const ZOOM_LEVELS: ZoomLevel[] = [50, 75, 100, 125, 150, 200]

export const ResponsivePdfPreviewModal: React.FC<
  ResponsivePdfPreviewModalProps
> = ({
  open,
  onClose,
  content,
  reportType,
  isLoading,
  allowDownload = false,
  allowPrint = false,
}) => {
  const [zoomLevel, setZoomLevel] = useState<ZoomLevel>(100)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const isMobile = useIsMobile()
  const contentRef = useRef<HTMLDivElement>(null)
  const modalRef = useRef<HTMLDivElement>(null)

  // Reset on modal open/close
  useEffect(() => {
    if (open) {
      setZoomLevel(100)
    }
  }, [open])

  const handleZoomIn = useCallback(() => {
    const currentIndex = ZOOM_LEVELS.indexOf(zoomLevel)
    if (currentIndex < ZOOM_LEVELS.length - 1) {
      setZoomLevel(ZOOM_LEVELS[currentIndex + 1])
    }
  }, [zoomLevel])

  const handleZoomOut = useCallback(() => {
    const currentIndex = ZOOM_LEVELS.indexOf(zoomLevel)
    if (currentIndex > 0) {
      setZoomLevel(ZOOM_LEVELS[currentIndex - 1])
    }
  }, [zoomLevel])

  const handlePrint = useCallback(() => {
    if (contentRef.current) {
      // Create a new window for printing with just the PDF content
      const printWindow = window.open('', '_blank')
      if (printWindow) {
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>${reportType} Report</title>
            <style>
              body { margin: 0; padding: 20px; font-family: Arial, sans-serif; }
              .print-content { width: 100%; }
              @media print { 
                body { padding: 0; }
                .print-content { margin: 0; }
              }
            </style>
          </head>
          <body>
            <div class="print-content">${content}</div>
          </body>
          </html>
        `)
        printWindow.document.close()
        printWindow.print()
      }
    }
  }, [content, reportType])

  const handleDownload = useCallback(() => {
    // Create a blob with the HTML content and trigger download
    const blob = new Blob([content], { type: 'text/html' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${reportType}_Report.html`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }, [content, reportType])

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true)
    // Simulate a small delay for visual feedback
    await new Promise((resolve) => setTimeout(resolve, 500))
    setIsRefreshing(false)
    // Reset zoom
    setZoomLevel(100)
  }, [])

  const canZoomIn = zoomLevel < ZOOM_LEVELS[ZOOM_LEVELS.length - 1]
  const canZoomOut = zoomLevel > ZOOM_LEVELS[0]

  const containerClasses = useMemo(() => {
    const classes = [styles.modalContainer]
    classes.push(styles[`zoomLevel${zoomLevel}`])
    return classes.join(' ')
  }, [zoomLevel])

  const renderToolbar = () => (
    <Box className={styles.toolbarContainer}>
      <Box className={styles.zoomControls}>
        <Tooltip title="Zoom Out">
          <span>
            <IconButton
              onClick={handleZoomOut}
              disabled={!canZoomOut || isLoading}
              size="small"
              className="zoomButton"
            >
              <ZoomOut />
            </IconButton>
          </span>
        </Tooltip>

        <Typography
          variant="body2"
          sx={{ minWidth: '60px', textAlign: 'center' }}
        >
          {zoomLevel}%
        </Typography>

        <Tooltip title="Zoom In">
          <span>
            <IconButton
              onClick={handleZoomIn}
              disabled={!canZoomIn || isLoading}
              size="small"
              className="zoomButton"
            >
              <ZoomIn />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Reset View">
          <IconButton
            onClick={handleRefresh}
            disabled={isLoading}
            size="small"
            className="zoomButton"
          >
            {isRefreshing ? <CircularProgress size={20} /> : <Refresh />}
          </IconButton>
        </Tooltip>
      </Box>

      {!isMobile && (allowPrint || allowDownload) && (
        <Box className={styles.actionButtons}>
          {allowPrint && (
            <Tooltip title="Print">
              <Button
                onClick={handlePrint}
                disabled={isLoading || !content}
                size="small"
                startIcon={<Print />}
                className="actionButton"
              >
                Print
              </Button>
            </Tooltip>
          )}

          {allowDownload && (
            <Tooltip title="Download">
              <Button
                onClick={handleDownload}
                disabled={isLoading || !content}
                size="small"
                startIcon={<Download />}
                className="actionButton"
              >
                Download
              </Button>
            </Tooltip>
          )}
        </Box>
      )}
    </Box>
  )

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box className={styles.loadingContainer}>
          <CircularProgress size={40} />
          <Typography variant="body2">Loading preview...</Typography>
        </Box>
      )
    }

    if (!content) {
      return (
        <Box className={styles.emptyState}>
          <Typography variant="h6" color="textSecondary">
            No content available
          </Typography>
          <Typography variant="body2" color="textSecondary">
            The report preview is not available at this time.
          </Typography>
        </Box>
      )
    }

    return (
      <Box className={styles.pdfViewport}>
        <div
          ref={contentRef}
          className={styles.pdfContent}
          title={`${reportType} Report Preview`}
          dangerouslySetInnerHTML={{
            __html: content,
          }}
        />
      </Box>
    )
  }

  return (
    <CustomModal
      open={open}
      closeButtonOnly
      header={`${reportType || 'Report'} Preview`}
      isLoadingState={isLoading}
      secondaryButtonAction={onClose}
      icon={<Visibility />}
      fitContentWidth={false}
    >
      <Box ref={modalRef} className={containerClasses}>
        {renderToolbar()}
        {renderContent()}

        {isMobile && (allowPrint || allowDownload) && (
          <Box
            sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'center' }}
          >
            {allowPrint && (
              <Button
                onClick={handlePrint}
                disabled={isLoading || !content}
                size="small"
                startIcon={<Print />}
                variant="outlined"
                fullWidth
              >
                Print
              </Button>
            )}

            {allowDownload && (
              <Button
                onClick={handleDownload}
                disabled={isLoading || !content}
                size="small"
                startIcon={<Download />}
                variant="outlined"
                fullWidth
              >
                Download
              </Button>
            )}
          </Box>
        )}
      </Box>
    </CustomModal>
  )
}
